import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionMessages,
  getColumns,
  getColumnsDependent,
  getRestoreDBParams,
  getStorageKey,
  modalConstants,
  navigationRoutes,
  NO_DATA_MESSAGE,
  restoreDbTypes,
  tabLabels,
} from "./constants";
import styles from "./RestoreDbTable.module.scss";
import clsx from "clsx";
import {
  RestoreDBModalModes,
  RestoreDbTypes,
  TableView,
} from "../../../enums/RestoreDatabaseEnums";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import RestoreDatabaseService from "../../../services/restoreDatabase.service";
import { message, Modal } from "antd";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";
import TNLoader from "../../../reusable-components/TNLoader";
import { ApiResponse } from "../../../enums/ApiResponse";
import { TableDefaults } from "../../../enums/TableEnums";

interface RestoreDbTableProps {
  activeDBView?: RestoreDbTypes;
  defaultPagination?: object;
  gettingDefaults: boolean;
}

const RestoreDbtable = (props?: RestoreDbTableProps) => {
  const navigate = useHistory();
  const queryClient = useQueryClient();
  const { GET_ARCHIVE_RECORDS_KEY } = QueryKeys;
  const { activeDBView, defaultPagination, gettingDefaults } = props;
  const {
    RESTORE_TITLE,
    DELETE_TITLE,
    MODAL_WIDTH,
    MODAL_OK_LABEL,
    RESTORE_CONTENT,
    DELETE_CONTENT,
  } = modalConstants;
  const {
    ARCHIVE_FAILURE,
    ARCHIVE_SUCCESS,
    DELETE_FAILURE,
    DELETE_SUCCESS,
    IS_PARENT_ARCHIVED,
    RESTORE_PARENT,
  } = ActionMessages;
  const { TAB_1_LABEL, TAB_2_LABEL } = tabLabels(activeDBView);
  const [activeView, setActiveView] = useState<TableView>(TableView.MAIN);
  const currentStorageKey = useMemo(() => 
    getStorageKey(activeDBView, activeView)
  , [activeDBView, activeView]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number| null>(() => {
    const raw = sessionStorage.getItem(currentStorageKey);
    return raw !== null && raw !== "" ? Number(raw) : null;
  });
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<RestoreDBModalModes>(
    RestoreDBModalModes.RESTORE
  );
  const [recordId, setRecordId] = useState<string>("");
  const [recordType, setRecordType] = useState<string>("");
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
    setRecordId("");
    setRecordType("");
  };
  const handleRowClick = (record) => {
    if (activeDBView === RestoreDbTypes.CDL_DRIVER) {
      return;
    }
    const route = navigationRoutes[activeDBView];
    navigate.push(`${route}/${record?.redirectId}`, { applyMask: true });
  };
  const handleAction = (
    id?: string,
    modalMode: RestoreDBModalModes,
    type?: string
  ) => {
    setRecordId(id);
    setModalMode(modalMode);
    handleModalOpen();
    if (type === "" && activeView === TableView.DEPENDENT) {
      setRecordType("record");
    } else {
      setRecordType(type);
    }
  };
  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setPageSizeChanged(true);
    try {  
      sessionStorage.setItem(currentStorageKey, size);
    } catch(err) {
      console.error("Err", err);
    }
  };

  const handlePagination = (page: number) => handleCurrentPage(page);

  const { GetArchivedRecords, RestoreArchiveRecord, DeleteArchiveRecord } =
    RestoreDatabaseService();
  const { data, refetch, isFetching, isSuccess: listFetchSuccess } = GetArchivedRecords(
    getRestoreDBParams(activeDBView, activeView, currentPage, pageSize),
    pageSizeChanged,
    !!sessionStorage.getItem(currentStorageKey)
  );

  const { mutateAsync: restoreArchiveRecord, isPending: isRestorePending } =
    RestoreArchiveRecord();
  const { mutateAsync: deleteArchiveRecord, isPending: isDeletePending } =
    DeleteArchiveRecord();

  const handleConfirm = async () => {
    let params = {
      id: recordId,
    };
    if (
      activeDBView === RestoreDbTypes.CDL_DRIVER ||
      (activeDBView === RestoreDbTypes.OWNER_OPERATOR &&
        activeView === TableView.MAIN)
    ) {
      params = {
        ...params,
        entityRef: restoreDbTypes[activeDBView],
      };
    }

    if (modalMode === RestoreDBModalModes.RESTORE) {
      await restoreArchiveRecord(params, {
        onSuccess: () => {
          message.success(ARCHIVE_SUCCESS[activeDBView]);
          handleModalClose();
          queryClient.invalidateQueries({
            queryKey: [GET_ARCHIVE_RECORDS_KEY],
          });
        },
        onError: (error) => {
          if (error?.response?.data?.message) {
            message.error(error?.response?.data?.message);
            return;
          }
          if (error?.response?.data?.message?.includes?.(IS_PARENT_ARCHIVED)) {
            message.error(
              RESTORE_PARENT[activeDBView] ?? error?.response?.data?.message
            );
            return;
          }
          message.error(ARCHIVE_FAILURE);
        },
      });
    }
    if (modalMode === RestoreDBModalModes.DELETE) {
      await deleteArchiveRecord(params, {
        onSuccess: () => {
          message.success(DELETE_SUCCESS[activeDBView]);
          handleModalClose();
          queryClient.invalidateQueries({
            queryKey: [GET_ARCHIVE_RECORDS_KEY],
          });
        },
        onError: (error) => {
          if (error?.response?.data?.message) {
            message.error(error?.response?.data?.message);
            return;
          }
          message.error(DELETE_FAILURE);
        },
      });
    }
  };

  useEffect(() => {
    if(
      defaultPagination?.message
      && defaultPagination?.message === ApiResponse.SUCCESS
    ) {
      setPageSize(defaultPagination?.data?.restore?.[currentStorageKey]);
    }
  }, [defaultPagination]);

  useEffect(() => {
    setCurrentPage(1);
    const raw = sessionStorage.getItem(currentStorageKey);
    const pageSizeValue = raw !== null && raw !== "" ? Number(raw) : 10;
    setPageSize(pageSizeValue);
  }, [currentStorageKey])

  useEffect(() => {
    if(pageSizeChanged && listFetchSuccess) {
      setPageSizeChanged(false);
    }
  }, [pageSizeChanged, listFetchSuccess])

  return (
    <>
      <TNLoader
        isLoading={
          isFetching
          || isRestorePending
          || isDeletePending
          || gettingDefaults
        } 
      />
      <Modal
        visible={isModalOpen}
        title={
          modalMode === RestoreDBModalModes.RESTORE
            ? RESTORE_TITLE
            : DELETE_TITLE
        }
        width={MODAL_WIDTH}
        okText={MODAL_OK_LABEL}
        maskClosable={false}
        onCancel={handleModalClose}
        onOk={handleConfirm}
      >
        {(() => {
          const content =
            modalMode === RestoreDBModalModes.RESTORE
              ? RESTORE_CONTENT[activeView][activeDBView]
              : DELETE_CONTENT[activeView][activeDBView];

          if (
            activeView === TableView.DEPENDENT &&
            typeof content === "function"
          ) {
            return content(recordType);
          }
          return content;
        })()}
      </Modal>
      <div className={styles.RestoreDbContainer}>
        <div>
          <ConditionalRender
            visible={activeDBView !== RestoreDbTypes.CDL_DRIVER}
          >
            <div
              className={clsx(styles.tabSwitch, {
                [styles.showDependent]: activeView === TableView.DEPENDENT,
              })}
            >
              <div
                className={clsx(styles.mainTableLabel, {
                  [styles.activeView]: activeView === TableView.MAIN,
                })}
                onClick={() => setActiveView(TableView.MAIN)}
              >
                {TAB_1_LABEL}
              </div>
              <div
                className={clsx(styles.dependentsTableLabel, {
                  [styles.activeView]: activeView === TableView.DEPENDENT,
                })}
                onClick={() => setActiveView(TableView.DEPENDENT)}
              >
                {TAB_2_LABEL}
              </div>
            </div>
          </ConditionalRender>
        </div>
        <div>
        <ConditionalRender
          visible={
            data?.data?.length > 0
            && !gettingDefaults
            && !isFetching
          }
          fallback={
            <div className={styles.emptyContainer}>
              <p>{NO_DATA_MESSAGE}</p>
            </div>
          }
          hideFallback={isFetching}
        >
          <CustomTableWithPagination
            columns={
              activeView === TableView.DEPENDENT &&
              activeDBView !== RestoreDbTypes.CDL_DRIVER
                ? getColumnsDependent({
                    activeDbView: activeDBView,
                    handleAction: handleAction,
                  })
                : getColumns({
                    activeDbView: activeDBView,
                    handleAction: handleAction,
                  })
            }
            data={data?.data ?? []}
            isClickableTable
            handleChange={handlePagination}
            handleSizeChangerChange={handlePaginationPageSize}
            totalData={data?.meta?.totalRecords}
            currentPage={currentPage}
            pageSize={pageSize ?? TableDefaults.PAGESIZE}
            handleSelectedRow={
              activeView === TableView.MAIN &&
              activeDBView !== RestoreDbTypes.CDL_DRIVER
                ? handleRowClick
                : {}
            }
          />
        </ConditionalRender>
        </div>
      </div>
    </>
  );
};
export default RestoreDbtable;
